// import { VertexBuffer } from '@babylonjs/core/Buffers/buffer';
// import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { FreeCamera } from '@babylonjs/core/Cameras/freeCamera';
import { ArcRotateCamera } from '@babylonjs/core/Cameras/arcRotateCamera';
// import { UniversalCamera } from '@babylonjs/core/Cameras/universalCamera';
import {
    Vector3, Vector2, Color4, Quaternion
} from '@babylonjs/core/Maths/math';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { PickingInfo } from '@babylonjs/core/Collisions/pickingInfo';
// To Enable Picking
import '@babylonjs/core/Culling/ray';

import { EXPORT_FILTER_PREFIX } from '../Tool/interface';
import {
    DegreeToRadianVector, IGeometryPoint, RadianToDegreeVector, mudolu2PI
} from '../../Utils/math';
import { SystemAnimation } from './systemAnimation';
import { Animation } from './Animation/animation';
import { Ease, EaseMode } from './Animation/animationEasing';

export interface IArcCameraAngles {
    alpha: number
    beta: number
    radius: number
}

export interface ICameraGeometryPoint extends IGeometryPoint {
    explore?: Vector2;
    radius?: number;
}

export const setArcCameraFromGeometryPoint = (
    camera: ArcRotateCamera,
    geometry: ICameraGeometryPoint
): void => {
    camera.position = geometry.position.clone();
    const rot = geometry.rotation.multiply(DegreeToRadianVector);
    camera.rotation = rot;
    const targetQuaternion = Quaternion.RotationYawPitchRoll(rot.y, rot.x, rot.z);
    camera.rotationQuaternion = targetQuaternion;
    if (camera.radius !== undefined) camera.radius = geometry.radius;
    camera.rebuildAnglesAndRadius();
    camera.setTarget(new Vector3(0, 0, 0));
};

export const setFreeCameraFromGeometryPoint = (
    camera: FreeCamera,
    geometry: ICameraGeometryPoint
): void => {
    camera.position.copyFrom(geometry.position);
    const rot = geometry.rotation.multiply(DegreeToRadianVector);
    camera.rotation = rot;
    const targetQuaternion = Quaternion.RotationYawPitchRoll(rot.y, rot.x, rot.z);
    camera.rotationQuaternion = targetQuaternion;
};

export class SystemCamera extends SystemAnimation {
    public parentCamera: TransformNode;

    public arcCamera: ArcRotateCamera;

    /**
    * how far can we see
    */
    public visibleWorld = 10;

    /**
     * Build all the essentials assets for the 3D Scene
     */
    constructor(canvas: HTMLCanvasElement) {
        super(canvas);

        this.parentCamera = new TransformNode(`${EXPORT_FILTER_PREFIX}_parent_camera`, this.scene);

        this.arcCamera = new ArcRotateCamera(`${EXPORT_FILTER_PREFIX}_main_arcCamera`, -Math.PI / 2, Math.PI / 2, 10, new Vector3(0, 0, 0), this.scene);
        this.arcCamera.lowerRadiusLimit = 0;
        this.arcCamera.upperRadiusLimit = 10;
        this.arcCamera.panningSensibility = 0;
        this.arcCamera.maxZ = this.visibleWorld * 1.5;
        this.arcCamera.minZ = 0;
        this.arcCamera.inputs.attached.keyboard.angularSpeed = 0.001;
        this.arcCamera.parent = this.parentCamera;
        this.scene.activeCamera = this.arcCamera;

        this.scene.clearColor = new Color4(0, 0, 0, 0);
        // this.scene.clearColor = new Color4(0.8, 0.8, 0.8, 1);

        // Some model need this camera when we load
        // So we force the import to prevent a bug
        // Check this model exported from adobe dimension: https://d2uret4ukwmuoe.cloudfront.net/jwcd03f6xls1djbx8aw/gbl/saasa.glb
        // Build test reveal it expand the viewer size of 40kB to import UniversalCamera
        // const test = new UniversalCamera('camera', Vector3.Zero(), this.scene);
        //! 08/11/2022 I have not the bug anymore

        this.setCameraTargetRotation(Vector3.Zero());
        this.resetHorizontalFixed();

        this.scene.detachControl();
        this.scene.constantlyUpdateMeshUnderPointer = false;
    }

    public pick(evt: MouseEvent, predicate?: (mesh: AbstractMesh) => boolean): PickingInfo {
        const canvasRect = this.scene.getEngine().getInputElementClientRect();
        if (!canvasRect) return null;
        const x = evt.clientX - canvasRect.left;
        const y = evt.clientY - canvasRect.top;
        const pickInfo = (predicate) ? this.scene.pick(x, y, predicate) : this.scene.pick(x, y);
        return pickInfo;
    }

    public pickMesh(evt: MouseEvent, predicate?: (mesh: AbstractMesh) => boolean): AbstractMesh {
        const pickInfo = this.pick(evt, predicate);
        return pickInfo.pickedMesh;
    }

    public getCurrentCameraGeometry(): ICameraGeometryPoint {
        const geo: ICameraGeometryPoint = this.getCurrentGeometry();
        geo.radius = this.arcCamera.radius;
        return geo;
    }

    public getCurrentGeometry(): IGeometryPoint {
        const pos = this.getCurrentPosition();
        const rot = this.getCurrentRotation();
        const geo: IGeometryPoint = {
            position: pos,
            rotation: rot
        };
        return geo;
    }

    public getAlphaBetaFromPositionRotation(geo: ICameraGeometryPoint): IArcCameraAngles {
        setArcCameraFromGeometryPoint(
            this.arcCamera,
            geo
        );
        return {
            alpha: mudolu2PI(this.arcCamera.alpha),
            beta: mudolu2PI(this.arcCamera.beta),
            radius: this.arcCamera.radius,
        };
    }

    public getCurrentPosition(): Vector3 {
        const pos = this.scene.activeCamera.position.clone();
        return pos;
    }

    public getCurrentRotation(): Vector3 {
        const quat = Quaternion.FromRotationMatrix(this.scene.activeCamera.getWorldMatrix());
        let rot = quat.toEulerAngles().clone();
        rot = rot.multiply(RadianToDegreeVector);
        return rot;
    }

    public bumpCamera(intensity: number): void {
        const animation = new Animation(this);
        animation.setEasing(Ease.Circle, EaseMode.InOut);
        const radiusStart = this.arcCamera.radius;
        animation.simple(20, (perc) => {
            const progress = Math.min(perc * 2, 2 - perc * 2);
            this.arcCamera.radius = radiusStart - intensity * progress;
        }, () => {
            this.arcCamera.radius = radiusStart;
        });
    }

    public focusOnArcCameraAngles(
        arcCameraAngles: IArcCameraAngles,
        callback?: () => void
    ): void {
        const animation = new Animation(this);
        animation.setEasing(Ease.Quadratic, EaseMode.InOut);
        const alphaStart = this.arcCamera.alpha;
        const betaStart = this.arcCamera.beta;
        const { radius, alpha, beta } = arcCameraAngles;
        const radiusStart = (radius) ? this.arcCamera.radius : 0;
        const alphaChange = mudolu2PI(alpha - this.arcCamera.alpha);
        const betaChange = mudolu2PI(beta - this.arcCamera.beta);
        const radiusChange = (radius) ? radius - this.arcCamera.radius : 0;
        if (!alphaChange && !betaChange && !radiusChange) {
            if (callback) callback();
            return;
        }
        animation.simple(100, (perc) => {
            this.arcCamera.alpha = alphaStart + alphaChange * perc;
            this.arcCamera.beta = betaStart + betaChange * perc;
            if (radius) this.arcCamera.radius = radiusStart + radiusChange * perc;
        }, () => {
            this.arcCamera.alpha = alpha;
            this.arcCamera.beta = beta;
            if (radius) this.arcCamera.radius = radius;
            if (callback) callback();
        });
    }

    public setCameraGeometry(geometry: ICameraGeometryPoint): void {
        this.setCameraPosition(geometry.position);
        const rot = geometry.rotation.multiply(DegreeToRadianVector);
        this.setCameraTargetRotation(rot);
        if (geometry.explore) this.setCameraExploreRotation(geometry.explore);
        if (geometry.radius) this.setRadius(geometry.radius);
    }

    position: Vector3;

    /**
    * Set the cameras position in the 3D scene
    * @param position Position to set
    */
    public setCameraPosition(position: Vector3): void {
        this.position = position;
        this.parentCamera.position = position;
    }

    private exploreRotation = Vector2.Zero();

    private setCameraExploreRotation(rotation: Vector2) {
        this.exploreRotation = rotation;
        this.checkRotation();
    }

    private targetRotation = Vector3.Zero();

    private setCameraTargetRotation(rotation: Vector3) {
        this.targetRotation = rotation;
        this.checkRotation();
    }

    private checkRotation() {
        this.checkArcTargetRotation();
    }

    private checkArcTargetRotation() {
        this.arcCamera.alpha = (this.targetRotation.y + this.exploreRotation.y * 2) - Math.PI / 2;
        this.arcCamera.beta = (-this.targetRotation.x + this.exploreRotation.x * 2) + Math.PI / 2;
    }

    public setRadius(radius: number): void {
        this.arcCamera.radius = radius;
    }
}
